import React from 'react';
import { createPortal } from 'react-dom';
import { AppIcon } from '../../icons/AppIcon';
import { cn } from '@/lib/utils';
import {
    useDateRangePicker,
    DateRange,
    DayState,
    getDayState,
    toIsoDate,
} from './useDateRangePicker';
import { cnFilterChip, type FilterChipSurface } from './filterChipStyles';

interface DateRangePickerProps {
    value: DateRange;
    onChange: (range: DateRange) => void;
    surface?: FilterChipSurface;
}

const WEEKDAY_LABELS = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'] as const;
const WEEKDAY_SHORT = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'] as const;

const PANEL_CLASS =
    'z-[9999] w-[min(100vw-2rem,21.25rem)] rounded-xl border border-border bg-card p-3 shadow-lg md:p-4';
const NAV_BTN =
    'inline-flex size-10 shrink-0 items-center justify-center rounded-lg text-muted transition-colors hover:bg-surface-subtle focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring';
const DAY_BTN =
    'mx-auto flex size-10 items-center justify-center rounded-full font-mono text-sm tabular-nums transition-colors touch-manipulation';

const dayClass = (state: DayState) => cn(
    DAY_BTN,
    state === 'endpoint' && 'bg-primary font-medium text-primary-foreground',
    state === 'range' && 'bg-brand-50 text-brand-600',
    state === 'default' && 'text-default hover:bg-surface-subtle',
);

const DateRangePicker: React.FC<DateRangePickerProps> = ({ value, onChange, surface }) => {
    const {
        isOpen,
        toggleOpen,
        clearRange,
        buttonRef,
        dropdownRef,
        dropdownPosition,
        handlePrevMonth,
        handleNextMonth,
        handleDayClick,
        monthMatrix,
        monthLabel,
        monthYearKey,
        displayLabel,
        presets,
    } = useDateRangePicker({ value, onChange });

    const monthHeadingId = `date-range-month-${monthYearKey}`;

    const isSegment = surface === 'segment';
    const triggerClass = surface
        ? cn(cnFilterChip(Boolean(value.start), surface), isSegment && 'w-full')
        : cn('chip min-h-11 w-full touch-manipulation md:min-h-7 md:w-auto', value.start && 'active');

    return (
        <div className={cn(
            'inline-flex min-w-0',
            isSegment ? 'w-full' : surface === 'pill' ? 'w-auto shrink-0' : 'w-full md:w-auto',
        )}>
            <div className={cn(triggerClass, isSegment && 'min-w-0')}>
                <button
                    ref={buttonRef}
                    type="button"
                    onClick={toggleOpen}
                    aria-expanded={isOpen}
                    aria-haspopup="dialog"
                    className={cn(
                        'inline-flex min-h-11 min-w-0 flex-1 items-center justify-center bg-transparent md:min-h-0',
                        surface ? 'gap-s1 text-inherit md:gap-s2' : 'gap-1 font-sans text-sm',
                    )}
                >
                    <AppIcon name="calendar" className="size-3.5 shrink-0" aria-hidden />
                    <span className="min-w-0 truncate">
                        <span className="md:hidden">{value.start ? displayLabel : 'Data'}</span>
                        <span className="hidden md:inline">{displayLabel}</span>
                    </span>
                    {!value.start && (
                        <AppIcon name="chevron-down" className="hidden size-3 shrink-0 opacity-70 md:block" aria-hidden />
                    )}
                </button>
                {value.start && (
                    <button
                        type="button"
                        aria-label="Limpar período"
                        className="chip-remove shrink-0"
                        onClick={(e) => {
                            e.stopPropagation();
                            clearRange();
                        }}
                    >
                        ×
                    </button>
                )}
            </div>

            {isOpen && createPortal(
                <div
                    ref={dropdownRef}
                    role="dialog"
                    aria-modal="false"
                    aria-labelledby={monthHeadingId}
                    style={{
                        position: 'fixed',
                        top: `${dropdownPosition.top}px`,
                        left: `${dropdownPosition.left}px`,
                    }}
                    className={PANEL_CLASS}
                >
                    <header className="mb-3 flex items-center justify-between md:mb-4">
                        <button type="button" onClick={handlePrevMonth} aria-label="Mês anterior" className={NAV_BTN}>
                            <AppIcon name="chevron-left" className="size-4" aria-hidden />
                        </button>
                        <time
                            id={monthHeadingId}
                            dateTime={monthYearKey}
                            className="font-sans text-base font-semibold capitalize text-default"
                        >
                            {monthLabel}
                        </time>
                        <button type="button" onClick={handleNextMonth} aria-label="Próximo mês" className={NAV_BTN}>
                            <AppIcon name="chevron-right" className="size-4" aria-hidden />
                        </button>
                    </header>

                    <table className="w-full table-fixed border-collapse" role="grid" aria-labelledby={monthHeadingId}>
                        <thead>
                            <tr>
                                {WEEKDAY_SHORT.map((short, index) => (
                                    <th
                                        key={WEEKDAY_LABELS[index]}
                                        scope="col"
                                        abbr={WEEKDAY_LABELS[index]}
                                        className="pb-2 font-sans text-xs font-medium text-muted"
                                    >
                                        {short}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {monthMatrix.map((week, weekIndex) => (
                                <tr key={weekIndex}>
                                    {week.map((day, dayIndex) => (
                                        <td key={dayIndex} className="h-10 p-0 text-center" role="presentation">
                                            {day ? (
                                                <button
                                                    type="button"
                                                    role="gridcell"
                                                    aria-selected={getDayState(day, value) !== 'default'}
                                                    aria-label={day.toLocaleDateString('pt-BR', {
                                                        weekday: 'long',
                                                        day: 'numeric',
                                                        month: 'long',
                                                        year: 'numeric',
                                                    })}
                                                    onClick={() => handleDayClick(day)}
                                                    className={dayClass(getDayState(day, value))}
                                                >
                                                    <time dateTime={toIsoDate(day)}>{day.getDate()}</time>
                                                </button>
                                            ) : null}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    <nav aria-label="Atalhos de período" className="mt-3 border-t border-border pt-3 md:mt-4 md:pt-4">
                        <ul className="flex flex-wrap gap-2">
                            {presets.map((preset) => (
                                <li key={preset.label}>
                                    <button
                                        type="button"
                                        onClick={preset.action}
                                        className={cn(
                                            'chip min-h-10 touch-manipulation md:min-h-7',
                                            displayLabel === preset.label && 'active',
                                        )}
                                    >
                                        {preset.label}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </nav>
                </div>,
                document.body,
            )}
        </div>
    );
};

export default DateRangePicker;
export type { DateRange };
